/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Tue Apr 16 2024
 **/

import { useCallback } from "react";

import { useStompManager } from "@/frameworks/libraries/network";
import { Topics } from "@/frameworks/core/commons";
import { useChat } from "@/frameworks/core/API";

import { useChatVisibility, useDMRoomActions, useSpaceChatRoomActions } from "./private";

/**
 * @description 채팅룸의 다루기위한 서비스 기능을 다루는 훅입니다.
 * TODO: 훅 이름 수정 예정
 */
export default function useChatRoomActions() {
	const stompNetwork = useStompManager();
	const chatAPI = useChat();

	const join = useCallback(async (roomID: string) => {
		const chatRoomData = (await chatAPI.getRoom.execute({ roomID: roomID })).data;
		if (chatRoomData?.deleted && !chatRoomData?.roomJoined) {
			leave(roomID);
		} else {
			stompNetwork.subscribe(Topics.Room(roomID));
		}
		return chatRoomData;
	}, []);

	const leave = async (roomID: string) => {
		stompNetwork.unsubscribe(Topics.Room(roomID), false);
	};

	const pin = async ({ roomID, pin }: { roomID: string; pin: boolean }) => {
		return (await chatAPI.pinRoom.execute({ roomId: roomID, pin })).data;
	};

	const updateNotification = async ({
		roomID,
		notificationType
	}: {
		roomID: string;
		notificationType: AlarmOptions;
	}) => {
		return await chatAPI.notification.execute({ roomId: roomID, notificationType });
	};

	const { open, getLastChat, close } = useChatVisibility();

	const common = {
		join,
		leave,
		pin,
		open,
		close,
		getLastChat,
		updateNotification
	};

	const dm = useDMRoomActions();
	const spaceChat = useSpaceChatRoomActions();

	return {
		common,
		dm,
		spaceChat
	};
}
